/**
 * ProfileModals - All modal dialogs for Profile page
 */
import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from "@/components/ui/dialog";
import { 
  User, Lock, Eye, EyeOff, Loader2, AlertTriangle, Crown, Heart, Users, Camera, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { isNativeApp, pickImage } from "@/utils/nativeBridge";
import { compressImage, validateImageFile, createPreviewUrl } from "@/utils/imageCompression";
import axios from "axios";
import { API } from "@/App";

// Edit Profile Modal - with native image upload
export const EditProfileModal = ({
  open,
  onOpenChange,
  editName,
  setEditName,
  currentAvatarUrl,
  profilePicturePath,
  savingProfile,
  onSubmit,
  onAvatarUploaded
}) => {
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const pendingRef = useRef(null);

  const doUpload = useCallback(async (base64, filename) => {
    setUploadFailed(false);
    setUploadingAvatar(true);
    setProgress(10);
    try {
      const response = await axios.post(
        `${API}/profile/picture/upload-base64`,
        { image: base64, filename, content_type: 'image/jpeg' },
        {
          timeout: 30000,
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 90) + 10);
          },
        }
      );
      setProgress(100);
      if (response.data.success) {
        toast.success("Profile picture updated!");
        if (onAvatarUploaded) onAvatarUploaded();
        pendingRef.current = null;
      }
    } catch (error) {
      setUploadFailed(true);
      toast.error("Upload failed. Tap retry to try again.");
    } finally {
      setUploadingAvatar(false);
      setTimeout(() => setProgress(0), 500);
    }
  }, [onAvatarUploaded]);

  const handleRetry = () => {
    if (pendingRef.current) {
      doUpload(pendingRef.current.base64, pendingRef.current.filename);
    }
  };

  const handleAvatarClick = async () => {
    if (uploadingAvatar) return;
    if (isNativeApp()) {
      try {
        const image = await pickImage({ allowsEditing: true, aspect: [1, 1], quality: 0.8 });
        if (!image) return;
        if (image.uri) setPreviewUrl(image.uri);
        const compressed = await compressImage(image.base64);
        pendingRef.current = { base64: compressed.base64, filename: image.fileName || 'profile.jpg' };
        doUpload(compressed.base64, image.fileName || 'profile.jpg');
      } catch (error) {
        toast.error("Failed to pick image");
        setPreviewUrl(null);
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const v = validateImageFile(file);
    if (!v.valid) { toast.error(v.error); return; }
    setPreviewUrl(createPreviewUrl(file));
    try {
      const compressed = await compressImage(file);
      pendingRef.current = { base64: compressed.base64, filename: file.name || 'profile.jpg' };
      doUpload(compressed.base64, file.name || 'profile.jpg');
    } catch (error) {
      toast.error("Failed to process image");
      setPreviewUrl(null);
    }
  };

  // Determine what image to show
  const displayImageUrl = previewUrl || 
    (profilePicturePath ? `${API}/profile/picture/${profilePicturePath}` : null) ||
    currentAvatarUrl;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        setPreviewUrl(null);
      }
    }}>
      <DialogContent className="max-w-md bg-card border-none rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-charcoal flex items-center gap-2">
            <User className="w-6 h-6 text-primary" />
            Edit Profile
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Update your profile information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6 mt-4">
          {/* Hidden file input for web uploads */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
            onChange={handleFileChange}
            className="hidden"
            data-testid="avatar-file-input"
          />
          
          {/* Avatar with upload - tap to change */}
          <div className="flex flex-col items-center cursor-pointer" onClick={uploadFailed ? handleRetry : handleAvatarClick}>
            <div className="relative group">
              {displayImageUrl ? (
                <img 
                  src={displayImageUrl} 
                  alt="Avatar preview"
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary/20"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`w-24 h-24 rounded-full bg-primary/10 items-center justify-center border-4 border-primary/20 ${displayImageUrl ? 'hidden' : 'flex'}`}
              >
                <User className="w-10 h-10 text-primary" />
              </div>

              {/* Progress ring */}
              {uploadingAvatar && progress > 0 && (
                <svg className="absolute inset-0 w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="44" fill="none" stroke="rgba(224,122,95,0.3)" strokeWidth="3" />
                  <circle cx="48" cy="48" r="44" fill="none" stroke="#E07A5F" strokeWidth="3" strokeDasharray={`${progress * 2.765} 276.5`} strokeLinecap="round" className="transition-all duration-300" />
                </svg>
              )}
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingAvatar ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : uploadFailed ? (
                  <RefreshCw className="w-6 h-6 text-white" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2">
              {uploadingAvatar ? "Uploading..." : uploadFailed ? "Tap to retry" : "Tap to change photo"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-name" className="text-charcoal font-medium">
              Name
            </Label>
            <Input
              id="edit-name"
              placeholder="Your name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="h-12 bg-background border-charcoal/10 rounded-xl"
              data-testid="edit-name-input"
            />
          </div>

          <DialogFooter className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-12 rounded-xl border-charcoal/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={savingProfile || uploadingAvatar}
              className="flex-1 h-12 bg-primary text-white hover:bg-primary/90 rounded-xl btn-press"
              data-testid="save-profile-btn"
            >
              {savingProfile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Change Password Modal
export const ChangePasswordModal = ({
  open,
  onOpenChange,
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  showCurrentPassword,
  setShowCurrentPassword,
  showNewPassword,
  setShowNewPassword,
  changingPassword,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-card border-none rounded-2xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-charcoal flex items-center gap-2">
          <Lock className="w-6 h-6 text-primary" />
          Change Password
        </DialogTitle>
        <DialogDescription className="text-muted-foreground">
          Enter your current password and choose a new one
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label htmlFor="current-password" className="text-charcoal font-medium">
            Current Password
          </Label>
          <div className="relative">
            <Input
              id="current-password"
              type={showCurrentPassword ? "text" : "password"}
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="h-12 bg-background border-charcoal/10 rounded-xl pr-12"
              data-testid="current-password-input"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-charcoal"
            >
              {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-password" className="text-charcoal font-medium">
            New Password
          </Label>
          <div className="relative">
            <Input
              id="new-password"
              type={showNewPassword ? "text" : "password"}
              placeholder="Enter new password (min. 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="h-12 bg-background border-charcoal/10 rounded-xl pr-12"
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-charcoal"
            >
              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-charcoal font-medium">
            Confirm New Password
          </Label>
          <Input
            id="confirm-password"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="h-12 bg-background border-charcoal/10 rounded-xl"
            data-testid="confirm-password-input"
          />
          {newPassword && confirmPassword && newPassword !== confirmPassword && (
            <p className="text-xs text-red-500">Passwords don't match</p>
          )}
        </div>

        <DialogFooter className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
            }}
            className="flex-1 h-12 rounded-xl border-charcoal/10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={changingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
            className="flex-1 h-12 bg-primary text-white hover:bg-primary/90 rounded-xl btn-press"
            data-testid="change-password-submit-btn"
          >
            {changingPassword ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Changing...
              </>
            ) : (
              "Change Password"
            )}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

// Delete Account Modal
export const DeleteAccountModal = ({
  open,
  onOpenChange,
  deletePassword,
  setDeletePassword,
  deleteConfirmText,
  setDeleteConfirmText,
  deleting,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-card border-none rounded-2xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-red-600 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6" />
          Delete Account
        </DialogTitle>
        <DialogDescription className="text-muted-foreground">
          This action is permanent and cannot be undone
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-6 mt-4">
        <div className="p-4 bg-red-50 rounded-xl border border-red-200">
          <p className="text-sm text-red-700 font-medium mb-2">
            You will lose:
          </p>
          <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
            <li>All your bucket list items</li>
            <li>Photos and memories</li>
            <li>Comments and reactions</li>
            <li>Partner connection</li>
            <li>Group memberships</li>
            <li>Achievements and badges</li>
          </ul>
        </div>

        <div className="space-y-2">
          <Label htmlFor="delete-password" className="text-charcoal font-medium">
            Enter your password
          </Label>
          <Input
            id="delete-password"
            type="password"
            placeholder="Your password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            className="h-12 bg-background border-charcoal/10 rounded-xl"
            data-testid="delete-password-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="delete-confirm" className="text-charcoal font-medium">
            Type <span className="font-bold text-red-600">DELETE</span> to confirm
          </Label>
          <Input
            id="delete-confirm"
            placeholder="Type DELETE"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
            className="h-12 bg-background border-charcoal/10 rounded-xl font-mono text-center"
            data-testid="delete-confirm-input"
          />
        </div>

        <DialogFooter className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setDeletePassword("");
              setDeleteConfirmText("");
            }}
            className="flex-1 h-12 rounded-xl border-charcoal/10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={deleting || deleteConfirmText !== "DELETE"}
            className="flex-1 h-12 bg-red-600 text-white hover:bg-red-700 rounded-xl"
            data-testid="confirm-delete-btn"
          >
            {deleting ? "Deleting..." : "Delete Forever"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

// Link Partner Modal
export const LinkPartnerModal = ({
  open,
  onOpenChange,
  inviteCode,
  setInviteCode,
  linking,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-card border-none rounded-2xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-charcoal">
          Link with Partner
        </DialogTitle>
        <DialogDescription className="text-muted-foreground">
          Enter your partner's invite code to connect your bucket lists
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-6 mt-4">
        <div className="space-y-2">
          <Label htmlFor="invite-code" className="text-charcoal font-medium">
            Partner's Invite Code
          </Label>
          <Input
            id="invite-code"
            placeholder="Enter code..."
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            className="h-12 bg-background border-charcoal/10 rounded-xl font-mono text-center text-lg tracking-widest"
            maxLength={8}
            data-testid="partner-invite-code-input"
          />
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-12 rounded-xl border-charcoal/10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={linking}
            className="flex-1 h-12 bg-coral text-white hover:bg-coral/90 rounded-xl btn-press"
            data-testid="submit-link-partner-btn"
          >
            {linking ? "Linking..." : "Link Partner"}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

// Unlink Partner Modal
export const UnlinkPartnerModal = ({
  open,
  onOpenChange,
  partnerName,
  removePartnerItems,
  setRemovePartnerItems,
  unlinking,
  onConfirm
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="w-5 h-5" />
          Unlink from Partner
        </DialogTitle>
        <DialogDescription>
          Are you sure you want to unlink from {partnerName}? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 space-y-4">
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={removePartnerItems}
              onChange={(e) => setRemovePartnerItems(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <div>
              <p className="font-medium text-foreground">Remove items they added</p>
              <p className="text-sm text-muted-foreground mt-1">
                Delete all bucket list items that {partnerName} created. 
                Leave unchecked to keep their contributions.
              </p>
            </div>
          </label>
        </div>
      </div>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          variant="outline"
          onClick={() => {
            onOpenChange(false);
            setRemovePartnerItems(false);
          }}
          className="rounded-xl"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          disabled={unlinking}
          className="rounded-xl"
          data-testid="confirm-unlink-btn"
        >
          {unlinking ? "Unlinking..." : removePartnerItems ? "Unlink & Remove Items" : "Unlink Partner"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Create Group Modal
export const CreateGroupModal = ({
  open,
  onOpenChange,
  groupName,
  setGroupName,
  creating,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-card border-none rounded-2xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-charcoal flex items-center gap-2">
          <Users className="w-6 h-6 text-secondary" />
          Create Group
        </DialogTitle>
        <DialogDescription className="text-muted-foreground">
          Create a new group to share bucket lists with friends
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-6 mt-4">
        <div className="space-y-2">
          <Label htmlFor="group-name" className="text-charcoal font-medium">
            Group Name
          </Label>
          <Input
            id="group-name"
            placeholder="e.g., Family Adventures"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="h-12 bg-background border-charcoal/10 rounded-xl"
            maxLength={50}
            data-testid="group-name-input"
          />
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-12 rounded-xl border-charcoal/10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={creating}
            className="flex-1 h-12 bg-secondary text-white hover:bg-secondary/90 rounded-xl btn-press"
            data-testid="submit-create-group-btn"
          >
            {creating ? "Creating..." : "Create Group"}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

// Join Group Modal
export const JoinGroupModal = ({
  open,
  onOpenChange,
  inviteCode,
  setInviteCode,
  joining,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-card border-none rounded-2xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-charcoal flex items-center gap-2">
          <Users className="w-6 h-6 text-secondary" />
          Join Group
        </DialogTitle>
        <DialogDescription className="text-muted-foreground">
          Enter the group's invite code to join
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-6 mt-4">
        <div className="space-y-2">
          <Label htmlFor="group-invite-code" className="text-charcoal font-medium">
            Group Invite Code
          </Label>
          <Input
            id="group-invite-code"
            placeholder="Enter code..."
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            className="h-12 bg-background border-charcoal/10 rounded-xl font-mono text-center text-lg tracking-widest"
            maxLength={8}
            data-testid="group-invite-code-input"
          />
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-12 rounded-xl border-charcoal/10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={joining}
            className="flex-1 h-12 bg-primary text-white hover:bg-primary/90 rounded-xl btn-press"
            data-testid="submit-join-group-btn"
          >
            {joining ? "Joining..." : "Join Group"}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

// Transfer Ownership Modal
export const TransferOwnershipModal = ({
  open,
  onOpenChange,
  group,
  userId,
  selectedNewAdmin,
  setSelectedNewAdmin,
  transferring,
  onConfirm
}) => (
  <Dialog open={open} onOpenChange={(isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setSelectedNewAdmin("");
    }
  }}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-yellow-500" />
          Transfer Ownership
        </DialogTitle>
        <DialogDescription>
          Choose a new admin for "{group?.name}". You will become a regular member.
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 space-y-4">
        <Label className="text-sm font-medium">Select New Admin</Label>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {group?.members
            ?.filter(m => m.id !== userId)
            .map((member) => (
              <label 
                key={member.id}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                  selectedNewAdmin === member.id 
                    ? "bg-primary/10 border-2 border-primary" 
                    : "bg-muted/30 border-2 border-transparent hover:bg-muted/50"
                }`}
              >
                <input
                  type="radio"
                  name="new-admin"
                  value={member.id}
                  checked={selectedNewAdmin === member.id}
                  onChange={() => setSelectedNewAdmin(member.id)}
                  className="sr-only"
                />
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <span className="text-sm font-medium text-secondary">
                    {member.name?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">{member.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{member.role}</p>
                </div>
                {selectedNewAdmin === member.id && (
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </label>
            ))}
        </div>
      </div>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          variant="outline"
          onClick={() => {
            onOpenChange(false);
            setSelectedNewAdmin("");
          }}
          className="rounded-xl"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={!selectedNewAdmin || transferring}
          className="rounded-xl bg-primary text-white hover:bg-primary/90"
          data-testid="confirm-transfer-btn"
        >
          {transferring ? "Transferring..." : "Transfer Ownership"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
